import { useCallback, useMemo, useState } from 'react';
import { FCX, Select } from '@datassential/platform-ui-lib';
import { RestaurantSegmentEnum } from '@enums';
import useLocalStorageState from '@hooks/useLocalStorageState';
import { RESTAURANT_SEGMENT_LABELS } from '@pages/Home/sections/HomePlaces/constants';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import HomeSectionTabs from '@pages/Home/components/HomeSectionTabs';
import HomeSectionLoader from '@pages/Home/components/HomeSectionLoader';
import { useTerritorySelection } from '@pages/Home/context/TerritorySelectionContext';
import { useLandscapeView } from '@pages/Home/context/LandscapeViewContext';
import { LandscapeTabEnum } from '@pages/Home/sections/HomeLandscape/HomeLandscape.constants';
import TerritoryScopeLabel from '@pages/Home/components/TerritoryScopeLabel/TerritoryScopeLabel';
import { useSalesFocusData } from '@apiHooks/useSalesFocusData';
import { useSalesOnSiteFocusData } from '@apiHooks/useSalesOnSiteFocusData';
import { useChainsWithMenu } from '@apiHooks/useChainsWithMenu';
import { useLandscapePublications, SALES_FOCUS_PUBLICATION_PACKAGES } from '@apiHooks/useLandscapePublications';
import { useRestaurantSegmentOptions } from '@apiHooks/useRestaurantSegmentOptions';
import LandscapeReadAboutIt from '@pages/Home/sections/HomeLandscape/LandscapeReadAboutIt';
import SalesFocusChainsTable from './SalesFocusChainsTable';
import SalesFocusIndependentsTable from './SalesFocusIndependentsTable';
import SalesFocusIndependentsSavedSearches from './SalesFocusIndependentsSavedSearches/SalesFocusIndependentsSavedSearches';
import SalesFocusOnSiteChainsTable from './SalesFocusOnSiteChainsTable';
import ChainMenuGap from './ChainMenuGap';
import { buildMoreChainTargetsUrl, buildMoreOnSiteTargetsUrl, buildMoreRestaurantsUrl } from './HomeSalesFocusChains.helpers';
import {
    BY_SIZE_LABEL,
    INDEPENDENTS_BY_LABEL,
    INDEPENDENTS_SEGMENTS,
    INDEPENDENTS_SUBSECTION_TITLE,
    INDEPENDENTS_TERRITORY_LABEL,
    ON_SITE_BY_SIZE_LABEL,
    ON_SITE_SUBSECTION_TITLE,
    SALES_FOCUS_TABS,
    SalesFocusTabEnum,
    SECTION_TITLE,
    SUBSECTION_TITLE,
    TERRITORY_LABEL,
} from './HomeSalesFocusChains.constants';
import './HomeSalesFocusChains.scss';

const HomeSalesFocusChains: FCX = () => {
    const { savedSelection } = useTerritorySelection();
    const { landscapeView } = useLandscapeView();
    const isOnSiteView = landscapeView === LandscapeTabEnum.ON_SITE;
    const [rawSegments, setRawSegments] = useLocalStorageState(
        'sales-focus-segments',
        RestaurantSegmentEnum.QUICK_SERVICE as string,
    );
    const segments = useMemo(
        () => {
            const parts = rawSegments.split(',').filter(Boolean) as RestaurantSegmentEnum[];
            return parts.length > 0 ? parts : [RestaurantSegmentEnum.QUICK_SERVICE];
        },
        [rawSegments],
    );
    const setSegments = useCallback(
        (segs: RestaurantSegmentEnum[]) => setRawSegments(segs.join(',')),
        [setRawSegments],
    );
    const [activeTab, setActiveTab] = useState<SalesFocusTabEnum>(SalesFocusTabEnum.CHAINS);

    const { data, isLoading: isDataLoading } = useSalesFocusData(savedSelection);
    const { data: publications, isLoading: isPublicationsLoading } = useLandscapePublications({ packages: SALES_FOCUS_PUBLICATION_PACKAGES });
    const { data: onSiteData, isLoading: isOnSiteLoading } = useSalesOnSiteFocusData(savedSelection);
    const { data: chainsWithMenu } = useChainsWithMenu();
    const { data: segmentOptions, isSuccess: segmentOptionsSuccess } = useRestaurantSegmentOptions();
    const isLoading = isOnSiteView
        ? isOnSiteLoading
        : isDataLoading || isPublicationsLoading;

    const moreTargetsUrl = useMemo(
        () => buildMoreChainTargetsUrl(segments, savedSelection),
        [segments, savedSelection],
    );

    const moreOnSiteTargetsUrl = useMemo(
        () => buildMoreOnSiteTargetsUrl(onSiteData.segments, savedSelection),
        [onSiteData.segments, savedSelection],
    );

    const independentsSegmentOptions = useMemo(() => {
        if (segmentOptionsSuccess === false || !segmentOptions.length) return INDEPENDENTS_SEGMENTS;
        const validEnumValues = new Set<string>(Object.values(RestaurantSegmentEnum));
        const labelToEnum: Record<string, string> = Object.fromEntries(
            Object.entries(RESTAURANT_SEGMENT_LABELS).map(([enumVal, label]) => [label, enumVal]),
        );
        const mapped = segmentOptions
            .map(opt => {
                const enumVal = labelToEnum[opt.name ?? ''];
                if (!enumVal || !validEnumValues.has(enumVal)) return null;
                return { id: enumVal as RestaurantSegmentEnum, title: opt.name ?? '' };
            })
            .filter(Boolean) as Array<{ id: RestaurantSegmentEnum; title: string }>;
        return mapped.length ? mapped : INDEPENDENTS_SEGMENTS;
    }, [segmentOptions, segmentOptionsSuccess]);

    const moreRestaurantsUrl = useMemo(
        () => (segmentOptionsSuccess === false
            ? data.independentsUrl
            : buildMoreRestaurantsUrl(data.independentsUrl, segments, segmentOptions)),
        [data.independentsUrl, segments, segmentOptions, segmentOptionsSuccess],
    );

    const topOnSiteCStore = useMemo(
        () =>
            [...(onSiteData.cStore ?? [])]
                .sort((a, b) => (b.units ?? 0) - (a.units ?? 0))
                .slice(0, 10),
        [onSiteData.cStore],
    );

    const topOnSiteGrocery = useMemo(
        () =>
            [...(onSiteData.grocery ?? [])]
                .sort((a, b) => (b.units ?? 0) - (a.units ?? 0))
                .slice(0, 10),
        [onSiteData.grocery],
    );

    const topOnSiteLodging = useMemo(
        () =>
            [...(onSiteData.lodging ?? [])]
                .sort((a, b) => (b.units ?? 0) - (a.units ?? 0))
                .slice(0, 10),
        [onSiteData.lodging],
    );

    const topChains = useMemo(
        () => {
            if (isOnSiteView) return [];
            const segmentLabels = new Set(
                segments.map((seg) => RESTAURANT_SEGMENT_LABELS[seg]),
            );
            return [...(data.chains ?? [])]
                .filter((c) =>
                    !segments.length || segmentLabels.has(c.segmentName ?? ''),
                )
                .sort((a, b) => (b.units ?? 0) - (a.units ?? 0))
                .slice(0, 10);
        },
        [data.chains, segments, isOnSiteView],
    );

    const topIndependents = useMemo(
        () => {
            if (isOnSiteView) return [];
            const segmentLabels = new Set(
                segments.map((seg) => RESTAURANT_SEGMENT_LABELS[seg]),
            );
            return [...(data.independents ?? [])]
                .filter((i) =>
                    !segments.length || segmentLabels.has(i.segmentName ?? ''),
                )
                .sort(
                    (a, b) => (b.totalPurchasePotential ?? 0) - (a.totalPurchasePotential ?? 0),
                )
                .slice(0, 10);
        },
        [data.independents, segments, isOnSiteView],
    );

    return (
        <div className="Home__wrapper">
            <div className="Home__sub-wrapper">
                <div className="HomeSalesFocusChains">
                <HomeSectionHeader>
                    <div className="HomeSalesFocusChains__header">
                        <HomeSectionTitle testId="textHomeSalesFocusChainsTitle">
                            {`${SECTION_TITLE}: `}<TerritoryScopeLabel />
                        </HomeSectionTitle>
                        {!isOnSiteView && (
                            <HomeSectionTabs
                                data={SALES_FOCUS_TABS}
                                activeTabId={activeTab}
                                onItemClick={(id) => setActiveTab(id as SalesFocusTabEnum)}
                                testId="blockHomeSalesFocusChainsTabs"
                            />
                        )}
                    </div>
                </HomeSectionHeader>

                {isLoading ? (
                    <HomeSectionLoader/>
                ) : isOnSiteView ? (
                    <>
                        <div className="HomeSalesFocusChains__sub-header HomeSalesFocusChains__sub-header--on-site">
                            <div className="HomeSalesFocusChains__sub-header-row">
                                <span className="HomeSalesFocusChains__subsection-title">
                                    {ON_SITE_SUBSECTION_TITLE}
                                </span>
                                <span className="HomeSalesFocusChains__by-size">{ON_SITE_BY_SIZE_LABEL}</span>
                            </div>
                        </div>
                        <div className="HomeSalesFocusChains__table-section HomeSalesFocusChains__table-section--on-site">
                            <SalesFocusOnSiteChainsTable
                                cStore={topOnSiteCStore}
                                grocery={topOnSiteGrocery}
                                lodging={topOnSiteLodging}
                                moreTargetsUrl={moreOnSiteTargetsUrl}
                            />
                        </div>
                    </>
                ) : activeTab === SalesFocusTabEnum.CHAINS ? (
                    <>
                        <div className="HomeSalesFocusChains__sub-header">
                            <div className="HomeSalesFocusChains__sub-header-row">
                                <span className="HomeSalesFocusChains__subsection-title">
                                    {SUBSECTION_TITLE}
                                </span>
                                <span className="HomeSalesFocusChains__by-size">{BY_SIZE_LABEL}</span>
                                <div className="HomeSalesFocusChains__segment-select-wrapper">
                                    <Select
                                        className="no-rem"
                                        value={segments}
                                        setValue={setSegments}
                                        options={independentsSegmentOptions}
                                        isMulti
                                        addCheckMarks
                                        dropdownProps={{ place: 'bottom-start' }}
                                    />
                                </div>
                            </div>
                            <span className="HomeSalesFocusChains__territory-label">
                                {TERRITORY_LABEL}
                            </span>
                        </div>

                        <div className="HomeSalesFocusChains__table-section">
                            <SalesFocusChainsTable
                                rows={topChains}
                                moreTargetsUrl={moreTargetsUrl}
                            />
                        </div>

                        <div className="HomeSalesFocusChains__bottom-row">
                            {publications.length > 0 && (
                                <div className="HomeSalesFocusChains__publications-wrapper">
                                    <LandscapeReadAboutIt publications={publications}/>
                                </div>
                            )}
                            <div className="HomeSalesFocusChains__menu-gap-wrapper">
                                <ChainMenuGap chains={chainsWithMenu}/>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="HomeSalesFocusChains__sub-header">
                            <div className="HomeSalesFocusChains__sub-header-row">
                                <span className="HomeSalesFocusChains__subsection-title">
                                    {INDEPENDENTS_SUBSECTION_TITLE}
                                </span>
                                <span className="HomeSalesFocusChains__by-size">{INDEPENDENTS_BY_LABEL}</span>
                                <div className="HomeSalesFocusChains__segment-select-wrapper">
                                    <Select
                                        className="no-rem"
                                        value={segments}
                                        setValue={setSegments}
                                        options={independentsSegmentOptions}
                                        isMulti
                                        addCheckMarks
                                        dropdownProps={{ place: 'bottom-start' }}
                                    />
                                </div>
                            </div>
                            <span className="HomeSalesFocusChains__territory-label">
                                {INDEPENDENTS_TERRITORY_LABEL}
                            </span>
                        </div>
                        <div className="HomeSalesFocusChains__table-section">
                            <SalesFocusIndependentsTable
                                rows={topIndependents}
                                moreRestaurantsUrl={moreRestaurantsUrl}
                            />
                        </div>
                        <SalesFocusIndependentsSavedSearches />
                    </>
                )}
                </div>
            </div>
        </div>
    );
};

export default HomeSalesFocusChains;
